import type { ComponentType, LazyExoticComponent } from 'react'
import { lazy } from 'react'

/**
 * Every workspace page, keyed by the url the server names it with.
 *
 * The server decides which of these a customer may reach; this map only says what to render once
 * it has. A row whose url is missing here is skipped rather than blanking the workspace.
 */
export const PAGE_COMPONENTS: Record<string, LazyExoticComponent<ComponentType>> = {
  '/bookkeeper': lazy(() => import('@/routes/Bookkeeper/pages/OverviewPage')),
  '/bookkeeper/transactions': lazy(() => import('@/routes/Bookkeeper/pages/TransactionsPage')),
  '/bookkeeper/tasks': lazy(() => import('@/routes/Bookkeeper/pages/TasksPage')),
  '/bookkeeper/reports': lazy(() => import('@/routes/Bookkeeper/pages/ReportsPage')),
  '/bookkeeper/chart-of-accounts': lazy(() => import('@/routes/Bookkeeper/pages/ChartOfAccountsPage')),
  '/bookkeeper/journal': lazy(() => import('@/routes/Bookkeeper/pages/JournalPage')),
  '/bookkeeper/general-ledger': lazy(() => import('@/routes/Bookkeeper/pages/GeneralLedgerPage')),
  '/bookkeeper/invoices': lazy(() => import('@/routes/Bookkeeper/pages/InvoicesPage')),
  '/bookkeeper/taxes': lazy(() => import('@/routes/Bookkeeper/pages/TaxEstimatesPage')),
  '/bookkeeper/mileage': lazy(() => import('@/routes/Bookkeeper/pages/MileagePage')),
  '/bookkeeper/time': lazy(() => import('@/routes/Bookkeeper/pages/TimeTrackingPage')),
}

/** The route segment under /bookkeeper for a url; empty for the overview. */
export function pageSegment(url: string): string {
  return url.replace(/^\/bookkeeper\/?/, '')
}

/** The optional product demo offered before meeting the bookkeeper. */
export const DEMO_PATH = '/bookkeeper/demo'
/** Where a customer starts or recovers the subscription required for their books. */
export const PAYMENT_PATH = '/bookkeeper/payment'

/** The onboarding call a customer books before their books open. Not a workspace page: it has no tab. */
export const SCHEDULE_PATH = '/bookkeeper/schedule'

/** Where a customer connects the account we keep their books from. Not a workspace page: it has no tab. */
export const CONNECT_PATH = '/bookkeeper/connect'

/** Where a visitor with no account starts, from their DOT number. Public: no session yet. */
export const ONBOARDING_PATH = '/bookkeeper/start'

/** The login page, told where to return once the visitor has signed in. */
export function loginHref(returnPath: string): string {
  return `/bookkeeper/login?next=${encodeURIComponent(returnPath)}`
}
